import React, {useMemo} from 'react';
import {Link, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useAuth} from "../../../providers/AuthProvider";
import {useProject} from "../../../providers/ClientProjectProvider";
import {PositionWizardLayout} from "../../project-position/PositionWizard";
import Icon from "../../../components/Icon";
import Project from "../../../models/Project";


const BriefRoleOverview = ({onUpdateRole, ...props}) => {

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const {auth} = useAuth();
    const {project} = useProject();
    const navigate = useNavigate();

    const positions = project.sortedPositions();
    const canAddRemoveProjectPositions = auth?.canAddRemoveProjectPositions(project);

    return (
        <div className="">
            <div className="flex justify-between">
                <h4 className="text-2xl">{positions.length} Positions</h4>

                <Link
                    to={{
                        pathname: project.pathForNewPosition(),
                        search: '?fromBrief=true'
                    }}
                    className={TB.mapClass("btn btn-primary border px-4",{ 'disabled-edit-link': !canAddRemoveProjectPositions })}
                    disabled={props.isDisabled || project.is_on_hold || project.hasRequestedSOW()}
                    onClick={async (e) => {
                        if (!canAddRemoveProjectPositions) {
                            e.preventDefault();
                            e.stopPropagation();
                            return TB.showPermissionError();
                        }
                        if (project.sortedPositions().length >= Project.maxAllowedPositions()) {

                            e.preventDefault();
                            e.stopPropagation()
                            return TB.sysAlert('byo.add_role_button_clicked_max_roles')
                        }

                        return navigate({
                            pathname: project.pathForNewPosition(),
                            search: '?fromBrief=true'
                        }, {
                            state: {
                                newPosition: true,
                                fromBrief: true
                            }
                        })
                    }}>
                    Add New Position
                </Link>
            </div>

            {positions.map(position => {
                return (
                    <div key={position.id} className="my-5 p-4 border border-light-gray5">

                        <div className="flex justify-between pb-6">

                            <h3 className="text-xl text-primary-100">
                                {position.title}
                            </h3>

                            <div className="flex gap-x-4">
                                <Link
                                    className="p-0.5 text-primary-100 underline"
                                    to={`role/${position.id}`}
                                >
                                    {position.hasBeenFilled() ? 'View':'Edit'}
                                </Link>

                                <button
                                    className={TB.mapClass("p-0.5 text-primary-100 underline",{ 'disabled-edit-link': !canAddRemoveProjectPositions })}
                                    disabled={props.isDisabled || project.is_on_hold || !project.canDeletePosition(position)}
                                    onClick={(e) => {
                                        if (!canAddRemoveProjectPositions) {
                                            return TB.showPermissionError();
                                        }
                                        if (project.sortedPositions(true).length <= 1) {
                                            return TB.toast('At lease 1 position is required to search for talent.')
                                        }
                                        project.confirmDeletePosition(position)
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>

                        <ul>
                            <li className="flex items-center pb-3">
                                <Icon name="calendar" className="mr-3" />
                                {TB.moment(position.started_at).format('ll')} - {TB.moment(position.ended_at).format('ll')}
                            </li>


                            {project.isTimeMaterials() ? (
                                <React.Fragment>
                                    <li className="flex items-center pb-3">
                                        <Icon name="clock" className="mr-3" />
                                        <p>{TB.pluralize("hour", position.avg_hours_per_period || 0, true)} / week avg</p>
                                    </li>

                                    <li className="flex items-center">
                                        <Icon name="building" className="mr-3" />
                                        <p>{TB.pluralize("day", position.onsite_days_per_week || 0, true)} / week on-site</p>
                                    </li>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {!!position.calculateBudgetBasedOnStrategy?.() && (
                                        <li className="flex items-center pb-3">
                                            <div className="flex flex-col items-center justify-center w-4 h-4 mr-3">
                                                <i className="far fa-money-bill-alt text-sm"/>
                                            </div>

                                            <p>{TB.formatCurrency(position.calculateBudgetBasedOnStrategy() || 10000)} Budget</p>
                                        </li>
                                    )}

                                    <li className="flex items-center">
                                        <div className="flex flex-col items-center justify-center w-4 h-4 mr-3">
                                            <i className="fas fa-tasks text-sm"/>
                                        </div>
                                        <p>{position.deliverables?.length} Deliverables{position.isFixedRetainer() && "/Activities"}</p>
                                    </li>
                                </React.Fragment>
                            )}
                        </ul>

                    </div>
                )
            })}
        </div>
    );
};


const BriefRoleEdit = (props) => {

    const {roleId} = useParams();
    const location = useLocation();
    // const {auth} = useAuth();
    const navigate = useNavigate();

    const {project, isLoading} = useProject();

    const positionIndex = TB.findIndex(project.sortedPositions(true), ['id', parseInt(roleId)]) + 1;
    const returnToOverview = () => navigate('./../', {replace: true});

    const editingRoleSubStep = useMemo(() => {
        return location.pathname.includes('skills') ||
            location.pathname.includes('industry-experience') ||
            location.pathname.includes('brands') ||
            location.pathname.includes('wripple-roles')
    }, [location.pathname]);

    return (
        <div className="text-left brief-role-scroll-container">

            <div>
                <Link className="p-0.5 text-primary-100" to={-1} replace={true}>
                    <i className="fas fa-arrow-left mr-2" />
                    <span className="underline">{editingRoleSubStep ? "Back to Edit Position" : "Back To All Positions"}</span>
                </Link>
            </div>

            {project.is_on_hold && (
                <div className="text-center my-2">
                    <p className="font-bold text-danger italic">
                        Note: Editing position requirements is disabled while project is on hold
                    </p>
                </div>
            )}
            {roleId && (
                <PositionWizardLayout
                    roleId={roleId}
                    positionIndex={positionIndex}
                    isDisabled={props.isDisabled}
                    goBack={returnToOverview}
                    onFinish={returnToOverview}/>
            )}
        </div>
        );
};
const BriefRoleTab = ({onUpdateRole, isDisabled, ...props}) => {

    // const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    // const {auth} = useAuth();
    // const {project} = useProject();
    return (
        <Routes>
            <Route index element={<BriefRoleOverview/>}/>
            <Route path={'role/:roleId/*'} element={<BriefRoleEdit onUpdateRole={onUpdateRole} isDisabled={isDisabled}/>}/>
            <Route path={'*'} element={<Navigate to={'./'} replace={true} state={location.state || {}}/>}/>
        </Routes>
    );
};



export default BriefRoleTab;